import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Sitemap } from '../components/Sitemap';
import { BookIcon, BookOpenIcon, CheckCircleIcon, PlayIcon, StarIcon, VolumeIcon, ArrowRightIcon, ListIcon, MessageSquareIcon, LightbulbIcon, GraduationCapIcon, FileTextIcon, DownloadIcon, CalendarIcon, ClockIcon, CheckIcon, PenToolIcon, HeadphonesIcon, UserIcon, BarChart2Icon, PuzzleIcon, ThumbsUpIcon, AlertCircleIcon, ImageIcon, SearchIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { NextPage } from '../components/NextPage';
const BEGINNER_TOPICS = [{
  id: 'greetings',
  title: {
    en: 'Greetings & Introductions',
    ar: 'التحيات والمقدمات'
  },
  description: {
    en: 'Learn essential phrases to introduce yourself and greet others',
    ar: 'تعلم العبارات الأساسية لتقديم نفسك وتحية الآخرين'
  },
  icon: MessageSquareIcon,
  examples: [{
    en: 'Hello, my name is...',
    ar: 'مرحباً، اسمي...'
  }, {
    en: 'Nice to meet you',
    ar: 'تشرفت بمقابلتك'
  }, {
    en: 'How are you today?',
    ar: 'كيف حالك اليوم؟'
  }],
  lessonCount: 3,
  completionTime: '30-45',
  level: 'absolute-beginner'
}, {
  id: 'basics',
  title: {
    en: 'Everyday Basics',
    ar: 'الأساسيات اليومية'
  },
  description: {
    en: 'Common words and phrases for daily situations',
    ar: 'الكلمات والعبارات الشائعة للمواقف اليومية'
  },
  icon: StarIcon,
  examples: [{
    en: 'Yes / No',
    ar: 'نعم / لا'
  }, {
    en: 'Please / Thank you',
    ar: 'من فضلك / شكراً'
  }, {
    en: 'Excuse me / Sorry',
    ar: 'عفواً / آسف'
  }],
  lessonCount: 5,
  completionTime: '45-60',
  level: 'beginner'
}, {
  id: 'numbers',
  title: {
    en: 'Numbers & Time',
    ar: 'الأرقام والوقت'
  },
  description: {
    en: 'Learn to count and tell time in English',
    ar: 'تعلم العد وقول الوقت باللغة الإنجليزية'
  },
  icon: ListIcon,
  examples: [{
    en: 'One, two, three...',
    ar: 'واحد، اثنان، ثلاثة...'
  }, {
    en: "It's 3:30 pm",
    ar: 'إنها الساعة 3:30 مساءً'
  }, {
    en: 'Monday, Tuesday...',
    ar: 'الاثنين، الثلاثاء...'
  }],
  lessonCount: 4,
  completionTime: '40-50',
  level: 'beginner'
}, {
  id: 'pronunciation',
  title: {
    en: 'Basic Pronunciation',
    ar: 'النطق الأساسي'
  },
  description: {
    en: 'Master the sounds of English letters and words',
    ar: 'أتقن أصوات الحروف والكلمات الإنجليزية'
  },
  icon: VolumeIcon,
  examples: [{
    en: 'Vowel sounds: A, E, I, O, U',
    ar: 'أصوات الحروف المتحركة: A, E, I, O, U'
  }, {
    en: 'Consonant sounds',
    ar: 'أصوات الحروف الساكنة'
  }, {
    en: 'Word stress patterns',
    ar: 'أنماط نبر الكلمات'
  }],
  lessonCount: 6,
  completionTime: '50-70',
  level: 'beginner'
}];
const DETAILED_LESSONS = [{
  id: 'greetings-1',
  topicId: 'greetings',
  title: {
    en: 'Basic Greetings',
    ar: 'التحيات الأساسية'
  },
  description: {
    en: 'Learn how to say hello and goodbye in different situations',
    ar: 'تعلم كيفية قول ��رحباً ووداعاً في مواقف مختلفة'
  },
  duration: '10 min',
  progress: 0,
  phrases: [{
    en: 'Hello',
    ar: 'مرحباً',
    audio: true
  }, {
    en: 'Good morning',
    ar: 'صباح الخير',
    audio: true
  }, {
    en: 'Good afternoon',
    ar: 'مساء الخير',
    audio: true
  }, {
    en: 'Goodbye',
    ar: 'وداعاً',
    audio: true
  }, {
    en: 'See you later',
    ar: 'أراك لاحقاً',
    audio: true
  }]
}, {
  id: 'greetings-2',
  topicId: 'greetings',
  title: {
    en: 'Introducing Yourself',
    ar: 'تقديم نفسك'
  },
  description: {
    en: 'Learn how to introduce yourself and ask basic questions',
    ar: 'تعلم كيفية تقديم نفسك وطرح أسئلة أساسية'
  },
  duration: '12 min',
  progress: 0,
  phrases: [{
    en: 'My name is...',
    ar: 'اسمي...',
    audio: true
  }, {
    en: "I'm from...",
    ar: 'أنا من...',
    audio: true
  }, {
    en: 'Nice to meet you',
    ar: 'تشرفت بمقابلتك',
    audio: true
  }, {
    en: 'What is your name?',
    ar: 'ما هو اسمك؟',
    audio: true
  }, {
    en: 'Where are you from?',
    ar: 'من أين أنت؟',
    audio: true
  }]
}, {
  id: 'greetings-3',
  topicId: 'greetings',
  title: {
    en: 'Small Talk',
    ar: 'محادثات قصيرة'
  },
  description: {
    en: 'Common questions and responses for brief conversations',
    ar: 'أسئلة وإجابات شائعة للمحادثات القصيرة'
  },
  duration: '15 min',
  progress: 0,
  phrases: [{
    en: 'How are you?',
    ar: 'كيف حالك؟',
    audio: true
  }, {
    en: "I'm fine, thank you",
    ar: 'أنا بخير، شكراً',
    audio: true
  }, {
    en: 'How is your day?',
    ar: 'كيف يومك؟',
    audio: true
  }, {
    en: 'What do you do?',
    ar: 'ماذا تعمل؟',
    audio: true
  }, {
    en: 'Do you like it here?',
    ar: 'هل يعجبك المكان هنا؟',
    audio: true
  }]
}, {
  id: 'basics-1',
  topicId: 'basics',
  title: {
    en: 'Common Expressions',
    ar: 'التعبيرات الشائعة'
  },
  description: {
    en: 'Essential words and phrases for everyday communication',
    ar: 'الكلمات والعبارات الأساسية للتواصل اليومي'
  },
  duration: '10 min',
  progress: 0,
  phrases: [{
    en: 'Yes / No',
    ar: 'نعم / لا',
    audio: true
  }, {
    en: 'Please',
    ar: 'من فضلك',
    audio: true
  }, {
    en: 'Thank you',
    ar: 'شكراً',
    audio: true
  }, {
    en: 'Excuse me',
    ar: 'عفواً',
    audio: true
  }, {
    en: "I don't understand",
    ar: 'لا أفهم',
    audio: true
  }]
}];
const LEARNING_RESOURCES = [{
  title: {
    en: 'Vocabulary Flashcards',
    ar: 'بطاقات المفردات'
  },
  description: {
    en: '100 essential words with pronunciation',
    ar: '100 كلمة أساسية مع النطق'
  },
  icon: BookOpenIcon,
  type: 'interactive',
  popular: true,
  format: 'interactive'
}, {
  title: {
    en: 'Grammar Basics PDF',
    ar: 'أساسيات القواعد PDF'
  },
  description: {
    en: 'Simple explanations of basic grammar',
    ar: 'شرح بسيط للقواعد الأساسية'
  },
  icon: FileTextIcon,
  type: 'download',
  popular: false,
  format: 'pdf'
}, {
  title: {
    en: 'Pronunciation Guide',
    ar: 'دليل النطق'
  },
  description: {
    en: 'Audio examples of common words',
    ar: 'أمثلة صوتية للكلمات الشائعة'
  },
  icon: VolumeIcon,
  type: 'interactive',
  popular: true,
  format: 'audio'
}, {
  title: {
    en: 'Practice Exercises',
    ar: 'تمارين تطبيقية'
  },
  description: {
    en: 'Interactive quizzes to test your knowledge',
    ar: 'اختبارات تفاعلية لاختبار معرفتك'
  },
  icon: CheckCircleIcon,
  type: 'interactive',
  popular: false,
  format: 'quiz'
}, {
  title: {
    en: 'Conversation Starters',
    ar: 'بدايات المحادثة'
  },
  description: {
    en: 'Common phrases to begin conversations',
    ar: 'عبارات شائعة لبدء المحادثات'
  },
  icon: MessageSquareIcon,
  type: 'download',
  popular: true,
  format: 'pdf'
}, {
  title: {
    en: 'Daily Practice Schedule',
    ar: 'جدول الممارسة اليومي'
  },
  description: {
    en: 'Structured plan for daily learning',
    ar: 'خطة منظمة للتعلم اليومي'
  },
  icon: CalendarIcon,
  type: 'download',
  popular: false,
  format: 'pdf'
}, {
  title: {
    en: 'Listening Practice',
    ar: 'تمارين الاستماع'
  },
  description: {
    en: 'Audio clips with comprehension questions',
    ar: 'مقاطع صوتية مع أسئلة الفهم'
  },
  icon: HeadphonesIcon,
  type: 'interactive',
  popular: false,
  format: 'audio'
}, {
  title: {
    en: 'Picture Dictionary',
    ar: 'قاموس مصور'
  },
  description: {
    en: 'Visual vocabulary with pronunciation',
    ar: 'مفردات مصورة مع النطق'
  },
  icon: BookIcon,
  type: 'interactive',
  popular: true,
  format: 'interactive'
}];
const NEXT_STEPS = [{
  title: {
    en: 'Daily Conversations',
    ar: 'المحادثات اليومية'
  },
  description: {
    en: 'Practice everyday dialogues',
    ar: 'تدرب على الحوارات اليومية'
  },
  icon: MessageSquareIcon
}, {
  title: {
    en: 'Grammar Foundations',
    ar: 'أساسيات القواعد'
  },
  description: {
    en: 'Learn basic sentence structures',
    ar: 'تعلم تراكيب الجمل الأساسية'
  },
  icon: BookIcon
}, {
  title: {
    en: 'Video Lessons',
    ar: 'دروس الفيديو'
  },
  description: {
    en: 'Watch guided video tutorials',
    ar: 'شاهد دروس الفيديو الإرشادية'
  },
  icon: PlayIcon
}];
const STUDY_TIPS = [{
  title: {
    en: 'Consistency is Key',
    ar: 'الاستمرارية هي المفتاح'
  },
  description: {
    en: 'Study for at least 15-20 minutes every day instead of several hours once a week.',
    ar: 'ادرس لمدة 15-20 دقيقة على الأقل كل يوم بدلاً من عدة ساعات مرة واحدة في الأسبوع.'
  },
  icon: CalendarIcon
}, {
  title: {
    en: 'Active Learning',
    ar: 'التعلم النشط'
  },
  description: {
    en: 'Speak out loud when practicing and try to use new words in sentences.',
    ar: 'تحدث بصوت عالٍ عند الممارسة وحاول استخدام الكلمات الجديدة في جمل.'
  },
  icon: VolumeIcon
}, {
  title: {
    en: 'Use All Senses',
    ar: 'استخدم جميع الحواس'
  },
  description: {
    en: 'Read, write, speak, and listen to reinforce your learning.',
    ar: 'اقر�� واكتب وتحدث واستمع لتعزيز ��علمك.'
  },
  icon: HeadphonesIcon
}, {
  title: {
    en: 'Track Progress',
    ar: 'تتبع التقدم'
  },
  description: {
    en: 'Keep a journal of new words and phrases you learn each day.',
    ar: 'احتفظ بمذكرة للكلمات والعبارات الجديدة التي تتعلمها كل يوم.'
  },
  icon: CheckIcon
}];
const COMMON_CHALLENGES = [{
  challenge: {
    en: "I can't remember new vocabulary",
    ar: 'لا أستطيع تذكر المفردات الجديدة'
  },
  solution: {
    en: 'Try using flashcards, mnemonics, or practice using new words in different contexts.',
    ar: 'جرب استخدام البطاقات التعليمية أو وسائل التذكر أو ممارسة استخدام الكلمات الجديدة في سياقات مختلفة.'
  }
}, {
  challenge: {
    en: "I'm afraid of making mistakes when speaking",
    ar: 'أخشى ارتكاب الأخطاء عند التحدث'
  },
  solution: {
    en: 'Remember that making mistakes is part of learning. Practice with supportive friends or language partners.',
    ar: 'تذكر أن ارتكاب الأخطاء جزء من التعلم. تدرب مع أصدقاء داعمين أو شركاء في اللغة.'
  }
}, {
  challenge: {
    en: "I don't understand when people speak quickly",
    ar: 'لا أفهم عندما يتحدث الناس بسرعة'
  },
  solution: {
    en: 'Start with slow audio materials and gradually increase speed. Watch videos with subtitles.',
    ar: 'ابدأ بالمواد الصوتية البطيئة وزد السرعة تدريجياً. شاهد مقاطع الفيديو مع الترجمات.'
  }
}];
const RESOURCES = [{
  title: {
    en: 'Vocabulary Flashcards',
    ar: 'بطاقات المفردات'
  },
  description: {
    en: '100 essential words with pronunciation',
    ar: '100 كلمة أساسية مع النطق'
  },
  icon: BookIcon,
  format: 'interactive',
  type: 'start',
  popular: false
}, {
  title: {
    en: 'Grammar Basics PDF',
    ar: 'أساسيات القواعد PDF'
  },
  description: {
    en: 'Simple explanations of basic grammar',
    ar: 'شرح بسيط للقواعد الأساسية'
  },
  icon: FileTextIcon,
  format: 'pdf',
  type: 'download',
  popular: false
}, {
  title: {
    en: 'Pronunciation Guide',
    ar: 'دليل النطق'
  },
  description: {
    en: 'Audio examples of common words',
    ar: 'أمثلة صوتية للكلمات الشائعة'
  },
  icon: HeadphonesIcon,
  format: 'audio',
  type: 'start',
  popular: true
}, {
  title: {
    en: 'Practice Exercises',
    ar: 'تمارين تطبيقية'
  },
  description: {
    en: 'Interactive quizzes to test your knowledge',
    ar: 'اختبارات تفاعلية لاختبار معرفتك'
  },
  icon: BookIcon,
  format: 'quiz',
  type: 'start',
  popular: true
}, {
  title: {
    en: 'Conversation Starters',
    ar: 'بدء المحادثات'
  },
  description: {
    en: 'Common phrases to begin conversations',
    ar: 'عبارات شائعة لبدء المحادثات'
  },
  icon: MessageSquareIcon,
  format: 'pdf',
  type: 'download',
  popular: false
}, {
  title: {
    en: 'Daily Practice Schedule',
    ar: 'جدول الممارسة اليومي'
  },
  description: {
    en: 'Structured plan for daily learning',
    ar: 'خطة منظمة للتعلم اليومي'
  },
  icon: CalendarIcon,
  format: 'pdf',
  type: 'download',
  popular: false
}, {
  title: {
    en: 'Listening Practice',
    ar: 'تمارين الاستماع'
  },
  description: {
    en: 'Audio clips with comprehension questions',
    ar: 'مقاطع صوتية مع أسئلة الفهم'
  },
  icon: HeadphonesIcon,
  format: 'audio',
  type: 'start',
  popular: true
}, {
  title: {
    en: 'Picture Dictionary',
    ar: 'القاموس المصور'
  },
  description: {
    en: 'Visual vocabulary with pronunciation',
    ar: 'مفردات مصورة مع النطق'
  },
  icon: ImageIcon,
  format: 'interactive',
  type: 'start',
  popular: false
}];
export function BeginnersGuidePage() {
  const {
    isArabic
  } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [resourceFilter, setResourceFilter] = useState('all');
  const [topic, setTopic] = useState(BEGINNER_TOPICS[0]);
  const [topics, setTopics] = useState(BEGINNER_TOPICS);
  const [nextTopic, setNextTopic] = useState(BEGINNER_TOPICS[1]);
  const [search, setSearch] = useState('');
  const filteredResources = RESOURCES.filter(resource => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return resource.title.en.toLowerCase().includes(searchLower) || resource.title.ar.includes(search) || resource.description.en.toLowerCase().includes(searchLower) || resource.description.ar.includes(search);
  });
  return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-2047">
      <Sitemap currentPage={{
      en: "Beginner's Guide",
      ar: 'دليل المبتدئين'
    }} breadcrumbs={[{
      label: {
        en: 'Resources',
        ar: 'المصادر'
      },
      href: '/resources'
    }]} data-id="element-2048" />
      <div className="max-w-7xl mx-auto" data-id="element-2049">
        {/* Header */}
        <div className="mb-12" data-id="element-2050">
          <div className="flex items-start gap-4 mb-6" data-id="element-2051">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-2052">
              <StarIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-2053" />
            </div>
            <div data-id="element-2054">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-2055">
                {isArabic ? 'دليل المبتدئين' : "Beginner's Guide"}
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300" data-id="element-2056">
                {isArabic ? 'ابدأ رحلة تعلم اللغة الإنجليزية بثقة مع مواردنا الشاملة للمبتدئين' : 'Start your English learning journey with confidence using our comprehensive beginner resources'}
              </p>
            </div>
          </div>
          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700 mb-8" data-id="element-2057">
            <div className="flex overflow-x-auto space-x-8" data-id="element-2058">
              <button className={`py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'overview' ? 'border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} onClick={() => setActiveTab('overview')} data-id="element-2059">
                {isArabic ? 'نظرة عامة' : 'Overview'}
              </button>
              <button className={`py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'lessons' ? 'border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} onClick={() => setActiveTab('lessons')} data-id="element-2060">
                {isArabic ? 'الدروس' : 'Lessons'}
              </button>
              <button className={`py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'resources' ? 'border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} onClick={() => setActiveTab('resources')} data-id="element-2061">
                {isArabic ? 'الموارد' : 'Resources'}
              </button>
            </div>
          </div>
        </div>
        {/* Main Content */}
        {activeTab === 'overview' && <div className="space-y-12" data-id="element-2062">
            {/* Introduction */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8" data-id="element-2063">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4" data-id="element-2064">
                {isArabic ? 'مرحباً بك في رحلة تعلم اللغة الإنجليزية' : 'Welcome to Your English Learning Journey'}
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-6" data-id="element-2065">
                {isArabic ? 'هذا الدليل مصمم خصيصًا لمساعدتك في بدء تعلم اللغة الإنجليزية من الصفر. سنبدأ بالأساسيات ونتقدم تدريجياً، مع شرح كل مفهوم باللغة العربية لضمان فهمك الكامل.' : "This guide is specially designed to help you start learning English from scratch. We'll begin with the basics and progress gradually, explaining each concept in Arabic to ensure your complete understanding."}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-2066">
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4" data-id="element-2067">
                  <div className="flex items-start gap-3" data-id="element-2068">
                    <ThumbsUpIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" data-id="element-2069" />
                    <div data-id="element-2070">
                      <h3 className="font-medium text-gray-900 dark:text-white mb-1" data-id="element-2071">
                        {isArabic ? 'لماذا هذا الدليل مناسب لك' : 'Why This Guide Is Right For You'}
                      </h3>
                      <p className="text-gray-800 dark:text-gray-200 text-sm" data-id="element-2072">
                        {isArabic ? 'مصمم خصيصًا للمتحدثين باللغة العربية، مع شرح واضح وأمثلة ذات صلة بالثقافة.' : 'Specially designed for Arabic speakers, with clear explanations and culturally relevant examples.'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4" data-id="element-2073">
                  <div className="flex items-start gap-3" data-id="element-2074">
                    <LightbulbIcon className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" data-id="element-2075" />
                    <div data-id="element-2076">
                      <h3 className="font-medium text-gray-900 dark:text-white mb-1" data-id="element-2077">
                        {isArabic ? 'نصيحة للمبتدئين' : 'Beginner Tip'}
                      </h3>
                      <p className="text-gray-800 dark:text-gray-200 text-sm" data-id="element-2078">
                        {isArabic ? 'خصص 20-30 دقيقة يومياً للدراسة للحصول على أفضل النتائج. الاتساق أهم من طول جلسات الدراسة.' : 'Dedicate 20-30 minutes daily to study for best results. Consistency is more important than length of study sessions.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-5 border-l-4 border-yellow-400 dark:border-yellow-600" data-id="element-2079">
                <div className="flex items-start gap-3" data-id="element-2080">
                  <AlertCircleIcon className="w-6 h-6 text-yellow-600 dark:text-yellow-400 flex-shrink-0" data-id="element-2081" />
                  <div data-id="element-2082">
                    <h3 className="font-medium text-gray-900 dark:text-white mb-2" data-id="element-2083">
                      {isArabic ? 'قبل أن تبدأ' : 'Before You Begin'}
                    </h3>
                    <ul className="space-y-2 text-gray-700 dark:text-gray-300" data-id="element-2084">
                      <li className="flex items-start gap-2" data-id="element-2085">
                        <CheckIcon className="w-4 h-4 text-green-500 mt-1" data-id="element-2086" />
                        <span data-id="element-2087">
                          {isArabic ? 'قم بإعداد دفتر ملاحظات أو تطبيق لتدوين الكلمات والعبارات الجديدة' : 'Set up a notebook or app to record new words and phrases'}
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-2088">
                        <CheckIcon className="w-4 h-4 text-green-500 mt-1" data-id="element-2089" />
                        <span data-id="element-2090">
                          {isArabic ? 'قم بتحميل تطبيق نطق للمساعدة في تعلم النطق الصحيح' : 'Download a pronunciation app to help learn correct pronunciation'}
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-2091">
                        <CheckIcon className="w-4 h-4 text-green-500 mt-1" data-id="element-2092" />
                        <span data-id="element-2093">
                          {isArabic ? 'حدد وقتًا منتظمًا للدراسة كل يوم لبناء العادة' : 'Set a regular time to study each day to build a habit'}
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            {/* What You'll Learn */}
            <div data-id="element-2094">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6" data-id="element-2095">
                {isArabic ? 'ماذا ستتعلم' : "What You'll Learn"}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2096">
                {BEGINNER_TOPICS.map(topic => <div key={topic.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6" data-id="element-2097">
                    <div className="flex items-center gap-3 mb-4" data-id="element-2098">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-2099">
                        <topic.icon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-2100" />
                      </div>
                      <div data-id="element-2101">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white" data-id="element-2102">
                          {isArabic ? topic.title.ar : topic.title.en}
                        </h3>
                        <div className="flex items-center gap-2 mt-1" data-id="element-2103">
                          <div className="flex items-center text-xs text-gray-500 dark:text-gray-400" data-id="element-2104">
                            <BookOpenIcon className="w-3.5 h-3.5 mr-1" data-id="element-2105" />
                            <span data-id="element-2106">
                              {topic.lessonCount}{' '}
                              {isArabic ? 'دروس' : 'lessons'}
                            </span>
                          </div>
                          <div className="flex items-center text-xs text-gray-500 dark:text-gray-400" data-id="element-2107">
                            <ClockIcon className="w-3.5 h-3.5 mr-1" data-id="element-2108" />
                            <span data-id="element-2109">
                              {topic.completionTime}{' '}
                              {isArabic ? 'دقيقة' : 'mins'}
                            </span>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${topic.level === 'absolute-beginner' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'}`} data-id="element-2110">
                            {topic.level === 'absolute-beginner' ? isArabic ? 'مبتدئ تماماً' : 'Absolute Beginner' : isArabic ? 'مبتدئ' : 'Beginner'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-2111">
                      {isArabic ? topic.description.ar : topic.description.en}
                    </p>
                    <div className="space-y-2 mb-4" data-id="element-2112">
                      {topic.examples.map((example, i) => <div key={i} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors" data-id="element-2113">
                          <span className="text-gray-900 dark:text-white" data-id="element-2114">
                            {example.en}
                          </span>
                          <span className="text-blue-600 dark:text-blue-400" dir="rtl" data-id="element-2115">
                            {example.ar}
                          </span>
                        </div>)}
                    </div>
                    <div className="flex items-center justify-between text-sm" data-id="element-2116">
                      <span className="text-gray-500 dark:text-gray-400" data-id="element-2117">
                        {topic.lessonCount} {isArabic ? 'دروس' : 'lessons'}
                      </span>
                      <Link to={`/resources/beginners-guide/${topic.id}`} className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1" data-id="element-2118">
                        <span data-id="element-2119">
                          {isArabic ? 'استكشاف الدروس' : 'Explore lessons'}
                        </span>
                        <ArrowRightIcon className="w-4 h-4" data-id="element-2120" />
                      </Link>
                    </div>
                  </div>)}
              </div>
            </div>
            {/* Learning Path */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-8" data-id="element-2121">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6" data-id="element-2122">
                {isArabic ? 'مسار التعلم الموصى به' : 'Recommended Learning Path'}
              </h2>
              <div className="relative" data-id="element-2123">
                {/* Timeline */}
                <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-blue-200 dark:bg-blue-800" data-id="element-2124"></div>
                <div className="space-y-8" data-id="element-2125">
                  {[1, 2, 3, 4].map(step => <div key={step} className="flex items-start gap-6" data-id="element-2126">
                      <div className="relative z-10" data-id="element-2127">
                        <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center border-2 border-blue-200 dark:border-blue-700" data-id="element-2128">
                          <span className="text-xl font-bold text-blue-600 dark:text-blue-400" data-id="element-2129">
                            {step}
                          </span>
                        </div>
                      </div>
                      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 flex-1" data-id="element-2130">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2" data-id="element-2131">
                          {isArabic ? ['تعلم التحيات والمقدمات', 'أتقن الأساسيات اليومية', 'تعلم الأرقام والوقت', 'تدرب على النطق الأساسي'][step - 1] : ['Learn Greetings & Introductions', 'Master Everyday Basics', 'Learn Numbers & Time', 'Practice Basic Pronunciation'][step - 1]}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300" data-id="element-2132">
                          {isArabic ? ['ابدأ بتعلم كيفية تقديم نفسك وتحية الآخرين.', 'تعلم الكلمات والعبارات الأساسية للمواقف اليومية.', 'أتقن الأرقام وكيفية قول الوقت والتاريخ.', 'تدرب على نطق الأصوات والكلمات الإنجليزية بشكل صحيح.'][step - 1] : ['Start by learning how to introduce yourself and greet others.', 'Learn essential words and phrases for everyday situations.', 'Master numbers and how to tell time and date.', 'Practice pronouncing English sounds and words correctly.'][step - 1]}
                        </p>
                        <div className="mt-4" data-id="element-2133">
                          <Link to={`/resources/beginners-guide/${['greetings', 'basics', 'numbers', 'pronunciation'][step - 1]}`} className="text-blue-600 dark:text-blue-400 hover:underline text-sm flex items-center gap-1" data-id="element-2134">
                            <span data-id="element-2135">
                              {isArabic ? 'ابدأ هذه الخطوة' : 'Start this step'}
                            </span>
                            <ArrowRightIcon className="w-4 h-4" data-id="element-2136" />
                          </Link>
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
            {/* Study Tips */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8" data-id="element-2137">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6" data-id="element-2138">
                {isArabic ? 'نصائح للدراسة' : 'Study Tips'}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-2139">
                {STUDY_TIPS.map((tip, index) => <div key={index} className="flex items-start gap-4" data-id="element-2140">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-2141">
                      <tip.icon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-2142" />
                    </div>
                    <div data-id="element-2143">
                      <h3 className="font-medium text-gray-900 dark:text-white mb-1" data-id="element-2144">
                        {isArabic ? tip.title.ar : tip.title.en}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300 text-sm" data-id="element-2145">
                        {isArabic ? tip.description.ar : tip.description.en}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
            {/* Common Challenges */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8" data-id="element-2146">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6" data-id="element-2147">
                {isArabic ? 'التحديات الشائعة وحلولها' : 'Common Challenges & Solutions'}
              </h2>
              <div className="space-y-6" data-id="element-2148">
                {COMMON_CHALLENGES.map((item, index) => <div key={index} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-5" data-id="element-2149">
                    <div className="flex items-start gap-3 mb-3" data-id="element-2150">
                      <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg mt-1" data-id="element-2151">
                        <AlertCircleIcon className="w-5 h-5 text-red-600 dark:text-red-400" data-id="element-2152" />
                      </div>
                      <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-2153">
                        {isArabic ? item.challenge.ar : item.challenge.en}
                      </h3>
                    </div>
                    <div className="ml-12" data-id="element-2154">
                      <div className="flex items-start gap-3" data-id="element-2155">
                        <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg mt-1" data-id="element-2156">
                          <LightbulbIcon className="w-5 h-5 text-green-600 dark:text-green-400" data-id="element-2157" />
                        </div>
                        <p className="text-gray-700 dark:text-gray-300" data-id="element-2158">
                          {isArabic ? item.solution.ar : item.solution.en}
                        </p>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
            {/* Next Steps */}
            <div data-id="element-2159">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6" data-id="element-2160">
                {isArabic ? 'الخطوات التالية' : 'Next Steps'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2161">
                {NEXT_STEPS.map((step, index) => <Link key={index} to={index === 0 ? '/resources/beginners-guide/conversations' : index === 1 ? '/resources/grammar' : '/resources/video-lessons'} className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow" data-id="element-2162">
                    <div className="flex items-center gap-3 mb-4" data-id="element-2163">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-2164">
                        <step.icon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-2165" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-2166">
                        {isArabic ? step.title.ar : step.title.en}
                      </h3>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-2167">
                      {isArabic ? step.description.ar : step.description.en}
                    </p>
                    <Link to="#" className="text-blue-600 dark:text-blue-400 hover:underline text-sm flex items-center gap-1" data-id="element-2168">
                      <span data-id="element-2169">{isArabic ? 'استكشف' : 'Explore'}</span>
                      <ArrowRightIcon className="w-4 h-4" data-id="element-2170" />
                    </Link>
                  </Link>)}
              </div>
            </div>
          </div>}
        {activeTab === 'lessons' && <div className="space-y-8" data-id="element-2171">
            {/* Topic Navigation */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 mb-8" data-id="element-2172">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4" data-id="element-2173">
                {isArabic ? 'مواضيع الدروس' : 'Lesson Topics'}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2174">
                {BEGINNER_TOPICS.map(topic => <Link key={topic.id} to={`/resources/beginners-guide/${topic.id}`} className="flex flex-col items-center p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-center" data-id="element-2175">
                    <topic.icon className="w-6 h-6 text-blue-600 dark:text-blue-400 mb-2" data-id="element-2176" />
                    <span className="font-medium text-gray-900 dark:text-white" data-id="element-2177">
                      {isArabic ? topic.title.ar : topic.title.en}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-2178">
                      {topic.lessonCount} {isArabic ? 'دروس' : 'lessons'}
                    </span>
                  </Link>)}
              </div>
            </div>
            {/* Featured Lessons */}
            <div className="mb-8" data-id="element-2179">
              <div className="flex items-center justify-between mb-6" data-id="element-2180">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-2181">
                  {isArabic ? 'الدروس المميزة' : 'Featured Lessons'}
                </h2>
                <Link to="#" className="text-blue-600 dark:text-blue-400 hover:underline text-sm flex items-center gap-1" data-id="element-2182">
                  <span data-id="element-2183">{isArabic ? 'عرض الكل' : 'View all'}</span>
                  <ArrowRightIcon className="w-4 h-4" data-id="element-2184" />
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2185">
                {DETAILED_LESSONS.slice(0, 3).map(lesson => <Link key={lesson.id} to={`/resources/beginners-guide/${lesson.topicId}/${lesson.id}`} className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-200" data-id="element-2186">
                    <div className={`h-2 ${lesson.topicId === 'greetings' ? 'bg-blue-500' : lesson.topicId === 'basics' ? 'bg-green-500' : 'bg-purple-500'}`} data-id="element-2187"></div>
                    <div className="p-6" data-id="element-2188">
                      <div className="flex items-center justify-between mb-4" data-id="element-2189">
                        <span className="text-xs font-medium px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400" data-id="element-2190">
                          {BEGINNER_TOPICS.find(t => t.id === lesson.topicId) ? isArabic ? BEGINNER_TOPICS.find(t => t.id === lesson.topicId)!.title.ar : BEGINNER_TOPICS.find(t => t.id === lesson.topicId)!.title.en : ''}
                        </span>
                        <span className="flex items-center text-xs text-gray-500 dark:text-gray-400" data-id="element-2191">
                          <ClockIcon className="w-3.5 h-3.5 mr-1" data-id="element-2192" />
                          {lesson.duration}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2" data-id="element-2193">
                        {isArabic ? lesson.title.ar : lesson.title.en}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300 text-sm mb-4" data-id="element-2194">
                        {isArabic ? lesson.description.ar : lesson.description.en}
                      </p>
                      <div className="flex justify-between items-center" data-id="element-2195">
                        <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2196">
                          {lesson.phrases.length}{' '}
                          {isArabic ? 'عبارات' : 'phrases'}
                        </div>
                        <button className="flex items-center gap-1 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg px-3 py-1.5 transition-colors" data-id="element-2197">
                          <PlayIcon className="w-4 h-4" data-id="element-2198" />
                          <span data-id="element-2199">{isArabic ? 'بدء الدرس' : 'Start Lesson'}</span>
                        </button>
                      </div>
                    </div>
                  </Link>)}
              </div>
            </div>
            {/* Lesson List */}
            <div className="space-y-6" data-id="element-2200">
              {BEGINNER_TOPICS.map(topic => <div key={topic.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden" data-id="element-2201">
                  <div className="p-6" data-id="element-2202">
                    <div className="flex items-center gap-3 mb-4" data-id="element-2203">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-2204">
                        <topic.icon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-2205" />
                      </div>
                      <div data-id="element-2206">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white" data-id="element-2207">
                          {isArabic ? topic.title.ar : topic.title.en}
                        </h3>
                        <div className="flex items-center gap-4 mt-1 text-sm" data-id="element-2208">
                          <span className="text-gray-500 dark:text-gray-400" data-id="element-2209">
                            {topic.lessonCount} {isArabic ? 'دروس' : 'lessons'}
                          </span>
                          <span className="text-gray-500 dark:text-gray-400" data-id="element-2210">
                            {topic.completionTime} {isArabic ? 'دقيقة' : 'mins'}{' '}
                            {isArabic ? 'تقريباً' : 'total'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 mb-6" data-id="element-2211">
                      {isArabic ? topic.description.ar : topic.description.en}
                    </p>
                    <div className="space-y-3" data-id="element-2212">
                      {DETAILED_LESSONS.filter(lesson => lesson.topicId === topic.id).map((lesson, i) => <div key={lesson.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-2213">
                          <div className="flex items-center gap-3" data-id="element-2214">
                            <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 font-medium" data-id="element-2215">
                              {i + 1}
                            </div>
                            <div data-id="element-2216">
                              <span className="font-medium text-gray-900 dark:text-white" data-id="element-2217">
                                {isArabic ? lesson.title.ar : lesson.title.en}
                              </span>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-2218">
                                {lesson.phrases.length}{' '}
                                {isArabic ? 'عبارات' : 'phrases'}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4" data-id="element-2219">
                            <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2220">
                              {lesson.duration}
                            </span>
                            <Link to={`/resources/beginners-guide/${topic.id}/${lesson.id}`} className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-full" data-id="element-2221">
                              <PlayIcon className="w-5 h-5" data-id="element-2222" />
                            </Link>
                          </div>
                        </div>)}
                      {/* Add placeholder lessons if there are fewer detailed lessons than the topic's lessonCount */}
                      {Array.from({
                  length: Math.max(0, topic.lessonCount - DETAILED_LESSONS.filter(lesson => lesson.topicId === topic.id).length)
                }).map((_, i) => {
                  const lessonIndex = DETAILED_LESSONS.filter(lesson => lesson.topicId === topic.id).length + i + 1;
                  return <div key={`placeholder-${topic.id}-${i}`} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-2223">
                            <div className="flex items-center gap-3" data-id="element-2224">
                              <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 font-medium" data-id="element-2225">
                                {lessonIndex}
                              </div>
                              <span className="font-medium text-gray-900 dark:text-white" data-id="element-2226">
                                {isArabic ? `درس ${lessonIndex}: ${['التدريب العملي', 'المراجعة والتقييم'][i % 2]}` : `Lesson ${lessonIndex}: ${['Practical Exercise', 'Review & Assessment'][i % 2]}`}
                              </span>
                            </div>
                            <div className="flex items-center gap-4" data-id="element-2227">
                              <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2228">
                                10-15 {isArabic ? 'دقائق' : 'min'}
                              </span>
                              <Link to={`/resources/beginners-guide/${topic.id}/lesson-${lessonIndex}`} className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-full" data-id="element-2229">
                                <PlayIcon className="w-5 h-5" data-id="element-2230" />
                              </Link>
                            </div>
                          </div>;
                })}
                    </div>
                  </div>
                </div>)}
            </div>
            {/* Learning Progress */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6" data-id="element-2231">
              <div className="flex items-center justify-between mb-6" data-id="element-2232">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white" data-id="element-2233">
                  {isArabic ? 'تق��م ��لتعلم' : 'Learning Progress'}
                </h2>
                <button className="text-sm text-blue-600 dark:text-blue-400 hover:underline" data-id="element-2234">
                  {isArabic ? 'عرض التقرير الكامل' : 'View Full Report'}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6" data-id="element-2235">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4" data-id="element-2236">
                  <div className="flex items-center gap-3" data-id="element-2237">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-2238">
                      <BookOpenIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-2239" />
                    </div>
                    <div data-id="element-2240">
                      <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2241">
                        {isArabic ? 'الدروس المكتملة' : 'Lessons Completed'}
                      </div>
                      <div className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-2242">
                        0/{DETAILED_LESSONS.length}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4" data-id="element-2243">
                  <div className="flex items-center gap-3" data-id="element-2244">
                    <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg" data-id="element-2245">
                      <ClockIcon className="w-5 h-5 text-green-600 dark:text-green-400" data-id="element-2246" />
                    </div>
                    <div data-id="element-2247">
                      <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2248">
                        {isArabic ? 'وقت الدراسة' : 'Study Time'}
                      </div>
                      <div className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-2249">
                        0 {isArabic ? 'دقيقة' : 'mins'}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4" data-id="element-2250">
                  <div className="flex items-center gap-3" data-id="element-2251">
                    <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg" data-id="element-2252">
                      <BarChart2Icon className="w-5 h-5 text-purple-600 dark:text-purple-400" data-id="element-2253" />
                    </div>
                    <div data-id="element-2254">
                      <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2255">
                        {isArabic ? 'التقدم العام' : 'Overall Progress'}
                      </div>
                      <div className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-2256">
                        0%
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 text-center" data-id="element-2257">
                <p className="text-gray-700 dark:text-gray-300" data-id="element-2258">
                  {isArabic ? 'ابدأ دروسك الآن للبدء في تتبع تقدمك!' : 'Start your lessons now to begin tracking your progress!'}
                </p>
                <Link to={`/resources/beginners-guide/${BEGINNER_TOPICS[0].id}/${DETAILED_LESSONS[0].id}`} className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-2259">
                  {isArabic ? 'ابدأ بأول درس' : 'Start First Lesson'}
                </Link>
              </div>
            </div>
          </div>}
        {activeTab === 'resources' && <div className="space-y-8" data-id="element-2260">
            {/* Resource Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 mb-8" data-id="element-2261">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4" data-id="element-2262">
                {isArabic ? 'تصفية الموارد' : 'Filter Resources'}
              </h2>
              <div className="flex flex-wrap gap-3" data-id="element-2263">
                <button onClick={() => setResourceFilter('all')} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${resourceFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`} data-id="element-2264">
                  {isArabic ? 'الكل' : 'All'}
                </button>
                <button onClick={() => setResourceFilter('popular')} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${resourceFilter === 'popular' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`} data-id="element-2265">
                  {isArabic ? 'الأكثر شعبية' : 'Popular'}
                </button>
                <button onClick={() => setResourceFilter('pdf')} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${resourceFilter === 'pdf' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`} data-id="element-2266">
                  PDF
                </button>
                <button onClick={() => setResourceFilter('audio')} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${resourceFilter === 'audio' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`} data-id="element-2267">
                  {isArabic ? 'صوتي' : 'Audio'}
                </button>
                <button onClick={() => setResourceFilter('interactive')} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${resourceFilter === 'interactive' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`} data-id="element-2268">
                  {isArabic ? 'تفاعلي' : 'Interactive'}
                </button>
                <button onClick={() => setResourceFilter('quiz')} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${resourceFilter === 'quiz' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`} data-id="element-2269">
                  {isArabic ? 'اختبارات' : 'Quizzes'}
                </button>
              </div>
            </div>
            {/* Resources Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-2270">
              {filteredResources.map((resource, index) => <Link key={index} to={resource.type === 'interactive' ? `/resources/beginners-guide/interactive/${index}` : `/resources/beginners-guide/download/${index}`} className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow relative flex flex-col" data-id="element-2271">
                  {resource.popular && <span className="absolute top-3 right-3 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 text-xs px-2 py-1 rounded-full" data-id="element-2272">
                      {isArabic ? 'شائع' : 'Popular'}
                    </span>}
                  <div className="flex items-center gap-3 mb-4" data-id="element-2273">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-2274">
                      <resource.icon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-2275" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-2276">
                      {isArabic ? resource.title.ar : resource.title.en}
                    </h3>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-6" data-id="element-2277">
                    {isArabic ? resource.description.ar : resource.description.en}
                  </p>
                  <div className="flex items-center justify-between mt-auto" data-id="element-2278">
                    <span className="text-xs text-gray-500 dark:text-gray-400 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-md" data-id="element-2279">
                      {resource.format === 'pdf' ? 'PDF' : resource.format === 'audio' ? isArabic ? 'صوتي' : 'Audio' : resource.format === 'interactive' ? isArabic ? 'تفاعلي' : 'Interactive' : isArabic ? 'اختبار' : 'Quiz'}
                    </span>
                    <button className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors text-sm
                        ${resource.type === 'download' ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/50'}`} data-id="element-2280">
                      {resource.type === 'download' ? <>
                          <DownloadIcon className="w-4 h-4" data-id="element-2281" />
                          <span data-id="element-2282">{isArabic ? 'تنزيل' : 'Download'}</span>
                        </> : <>
                          <PlayIcon className="w-4 h-4" data-id="element-2283" />
                          <span data-id="element-2284">{isArabic ? 'بدء' : 'Start'}</span>
                        </>}
                    </button>
                  </div>
                </Link>)}
            </div>
          </div>}
        {/* Search Bar */}
        <div className="relative max-w-xl mx-auto mb-8" data-id="element-2285">
          <input type="text" placeholder={isArabic ? 'ابحث عن المصادر...' : 'Search resources...'} value={search} onChange={e => setSearch(e.target.value)} className="w-full px-4 py-2 pl-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400" data-id="element-2286" />
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" data-id="element-2287" />
        </div>
        {/* Next Page Navigation */}
        <div className="mt-12" data-id="element-2288">
          <NextPage currentPage={topic.id} totalPages={topics.length} nextPageUrl={`/resources/beginners-guide/${nextTopic?.id || ''}`} prevPageUrl="/resources/beginners-guide" nextTitle={{
          en: nextTopic ? nextTopic.title.en : 'Return to Guide',
          ar: nextTopic ? nextTopic.title.ar : 'العودة إلى الدليل'
        }} nextDescription={{
          en: nextTopic ? 'Continue learning with the next topic' : 'Browse all beginner topics',
          ar: nextTopic ? 'واصل التعلم مع الموضوع التالي' : 'تصفح جميع المواضيع للمبتدئين'
        }} isLastPage={!nextTopic} data-id="element-2289" />
        </div>
      </div>
    </div>;
}